/**
 * Manages a PixiJS 8 Application lifecycle: creation, resize, and destruction.
 * Used by both SceneView and LivePreview canvases.
 */
import { Application } from 'pixi.js';

export interface PixiAppManagerOptions {
  /** Background alpha (0 = transparent, 1 = opaque). Default: 0 */
  backgroundAlpha?: number;
  /** Background color (hex number). Default: 0x09090b (zinc-950) */
  backgroundColor?: number;
  /** Enable antialiasing. Default: true */
  antialias?: boolean;
}

export class PixiAppManager {
  private app: Application | null = null;
  private container: HTMLDivElement | null = null;

  get application(): Application | null {
    return this.app;
  }

  get canvas(): HTMLCanvasElement | null {
    return this.app?.canvas as HTMLCanvasElement | null;
  }

  get isReady(): boolean {
    return this.app !== null;
  }

  async init(
    container: HTMLDivElement,
    width: number,
    height: number,
    options: PixiAppManagerOptions = {}
  ): Promise<Application> {
    if (this.app) {
      this.destroy();
    }

    this.container = container;
    const app = new Application();

    await app.init({
      width,
      height,
      backgroundAlpha: options.backgroundAlpha ?? 0,
      background: options.backgroundColor ?? 0x09090b,
      antialias: options.antialias ?? true,
      resolution: window.devicePixelRatio || 1,
      autoDensity: true,
    });

    // Style the canvas to fill the container
    const canvas = app.canvas as HTMLCanvasElement;
    canvas.style.display = 'block';
    canvas.style.width = '100%';
    canvas.style.height = '100%';

    container.appendChild(canvas);
    this.app = app;

    return app;
  }

  resize(width: number, height: number): void {
    if (!this.app) return;
    this.app.renderer.resize(width, height);
  }

  destroy(): void {
    if (this.app) {
      // Remove canvas from DOM
      const canvas = this.app.canvas as HTMLCanvasElement;
      if (canvas.parentElement) {
        canvas.parentElement.removeChild(canvas);
      }
      this.app.destroy(true, { children: true, texture: true });
      this.app = null;
    }
    this.container = null;
  }
}
